import * as dayjs from 'dayjs';
import { faCalendar, faExclamationCircle, faPlus, faMinus } from '@fortawesome/free-solid-svg-icons';
import { NgForm } from '@angular/forms';
import { NgbAccordion } from '@ng-bootstrap/ng-bootstrap';
import { Component, ViewEncapsulation, Input, OnInit, OnChanges, Output, EventEmitter, ViewChild } from '@angular/core';
import { get, find, findIndex, cloneDeep, map, filter, without, merge, includes } from 'lodash';
import SelfPayFormDependent from 'src/app/models/selfPayFormDependent';
import Member from 'src/app/models/member';
import SelfPay from 'src/app/models/selfPay';
import { CoreService } from 'src/app/services/core.service';
import MemberAddress from 'src/app/models/memberAddress';
import RelationshipType from 'src/app/models/relationshipType';
import RelationshipQualifyReason from 'src/app/models/relationshipQualifyReason';
import SelfPayForm from 'src/app/models/selfPayForm';
import { env } from 'src/env/development';

@Component({
  selector: 'self-pay-dependent',
  templateUrl: 'selfPay.dependent.component.html',
  styleUrls: [],
  providers: [],
  encapsulation: ViewEncapsulation.None
})
export class SelfPayDependentComponent implements OnChanges, OnInit {
  @Input() selfPay: SelfPay;
  @Input() currentDependents: Member[];
  @Input() agency: string;
  @Input() lookups = {
    selfPayTypes: [],
    documentTypes: [],
    genderIdentities: [],
    birthSexes: [],
    countries: [],
    counties: [],
    relationshipTypes: [],
    relationshipQualifyReasons: []
  };
  @Output() saveSelfPay: EventEmitter<any> = new EventEmitter<any>();
  @Output() previous: EventEmitter<void> = new EventEmitter();
  existingDependents: Member[] = [];
  newDependents: SelfPayFormDependent[] = [];
  showSpouse = false;
  showChild = false;
  @ViewChild('dependentAccord') dependentAccord: NgbAccordion;
  icons = {
      faCalendar,
      faExclamationCircle,
      faPlus,
      faMinus
  };
  activeIds = [];
  filteredRelationshipQualifyReasons: string[][];

  constructor(private coreService: CoreService) { }

  ngOnChanges(): void {
    // if initial time through
    if (!this.selfPay.formJson.dependents) {
      if (this.currentDependents.length > 0) {
        this.selfPay.formJson.dependents = map(this.currentDependents, (cd) => {
          const dep = new SelfPayFormDependent(cd);
          dep.applyDefaults(this.selfPay, this.selfPay.formJson.subscriber);

          return dep;
        });
      } else {
        this.selfPay.formJson.dependents = [];
      }
    }

    if (!this.filteredRelationshipQualifyReasons)
      this.selfPay.formJson.dependents.forEach(dep => {
        let allowedQualReasons: string[] = [];
        if (!dep.memberId && dep.relationshipTypeCode)
          allowedQualReasons = this.allowedRelationshipQualifyReasons(dep);

        (this.filteredRelationshipQualifyReasons ??= []).push(allowedQualReasons);
      });
  }

  ngOnInit(): void {
    this.activeIds = map(this.selfPay.formJson.dependents, (d: SelfPayFormDependent) => d.socialSecurityNumber);
  }

  addNewDependent(): void {
    const newDep = new SelfPayFormDependent({});
    newDep.applyDefaults(this.selfPay, this.selfPay.formJson.subscriber);

    this.selfPay.formJson.dependents.push(newDep);
    this.activeIds.push(null);
    (this.filteredRelationshipQualifyReasons ??= []).push([])

    setTimeout(() => {
      const nd = document.getElementById('newDep');
      nd.scrollIntoView();
    });
  }

  emitSelfPay(e): void {
    this.saveSelfPay.emit(e);
  }

  pushDependent(dependent, index, dep, thisForm: NgForm, formContainer: any): void {
    this.markAllControlsAsTouched(thisForm);
    dependent.confirmed = true;
    if (thisForm.valid) {
      if (!dependent.memberId && !dependent.medicalCoverInd && !dependent.dentalCoverInd) {
        return this.coreService.popMessage('You must make a coverage election for a newly added dependent.', 'error', 3000);
      }
      if ((this.selfPay.isRetireeOE || this.selfPay.isPebbCobraOE) && !dependent.medicalCoverInd && !dependent.dentalCoverInd && !dependent.visionCoverInd && !dependent.terminateAllCoverageInd) {
        return this.coreService.popMessage('You must make a coverage election or choose to terminate for this dependent.', 'error', 3000);
      }
      this.selfPay.formJson.dependents.splice(index, 1, dependent);
      // do not advance step when saving deps
      this.saveSelfPay.emit({selfPay: this.selfPay, skipAdvance: true});
      if(!this.selfPay.isPebbCobraOE && !this.selfPay.isRetireeOE) {
        this.dependentAccord.collapseAll();
      }else {
        const activeIdIdx = findIndex(this.activeIds, (d) => d == dependent.socialSecurityNumber);
        this.activeIds.splice(activeIdIdx, 1)
      }
      const accordForm = document.getElementById('accordTitle');
      accordForm.scrollIntoView();
    }
    else {
      this.coreService.popMessage('Please complete required fields.', 'error', 4000, this.coreService.getInvalidFields(formContainer));
    }
  }

  collapseAccordAndRemoveDep(): void {
    this.selfPay.formJson.dependents = filter(this.selfPay.formJson.dependents, (d) => d.memberId || d.firstName || d.lastName);
  }

  markAllControlsAsTouched(thisForm: NgForm): void {
    this.coreService.markFormControlsAsTouched(thisForm);
  }
  
  changeRelationshipType(dependentIndex: number, relationshipTypeCode: string): void {
    const dep = this.selfPay.formJson.dependents[dependentIndex];
    dep.relationshipQualifyReasonId = null;

    this.updateBirthDate(dependentIndex, dep);
  }

  updateBirthDate(dependentIndex: number, dep: SelfPayFormDependent): void {
    if(dep.relationshipTypeCode !== 'S')
      dep.relationshipQualifyReasonId = this.determineRelationshipQualifyReason(dep);
  
    this.filteredRelationshipQualifyReasons[dependentIndex] = this.allowedRelationshipQualifyReasons(dep);
  }

  allowedRelationshipQualifyReasons(dep: SelfPayFormDependent): string[] {
    if (dep.relationshipTypeCode) {
      const ageInYears = dayjs().diff(dayjs(dep.birthDate), 'year');

      if (ageInYears > 26)
        return env.rtToQrMap26[dep.relationshipTypeCode];
      else
        return env.rtToQrMap[dep.relationshipTypeCode];
    }
  }

  determineRelationshipQualifyReason(dep: SelfPayFormDependent): string {
    if (dep.relationshipTypeCode) {
      const allowedRelationshipQualifyReasons = this.allowedRelationshipQualifyReasons(dep);
      return get(
        find(this.lookups.relationshipQualifyReasons, (rq: RelationshipQualifyReason) => allowedRelationshipQualifyReasons.includes(rq.relationshipQualifyReasonCode)),
        'relationshipQualifyReasonId'
      );
    }

    return null;
  }
}
